// Stock Transfer Types
export interface StockTransfer {
  _id: string;
  transfer_id: string;
  transfer_number: string;
  transfer_type: "Inter-Branch" | "Internal";

  // Location Information
  source_location: {
    branch_id: string;
    branch_name: string;
    warehouse_id: string;
    warehouse_name: string;
  };

  target_location: {
    branch_id: string;
    branch_name: string;
    warehouse_id: string;
    warehouse_name: string;
  };

  // Transfer Details
  items: StockTransferItem[];
  expected_delivery_date?: number;
  notes?: string;
  reason?: string; // Required for internal transfers

  // Status and Workflow
  status: "Draft" | "Dispatched" | "In Transit" | "Received" | "Cancelled";
  workflow: {
    created_by: string;
    created_at: number;
    dispatched_by?: string;
    dispatched_at?: number;
    received_by?: string;
    received_at?: number;
    cancelled_by?: string;
    cancelled_at?: number;
    cancellation_reason?: string;
  };

  // Audit Fields
  business_id: string;
  source_branch_id: string;
  target_branch_id: string;
  created_at: number;
  updated_at: number;
  created_by: string;
  updated_by: string;
}

export interface StockTransferItem {
  _id: string;
  item_id: string;
  product_id: string;
  ingredient_id?: string;
  item_name: string;
  sku: string;

  // Quantity Information
  quantity: number;
  unit: string;

  // Batch Information (for batch-tracked items)
  is_batch_tracked: boolean;
  batch_id?: string;
  batch_number?: string;
  expiry_date?: number;

  // Cost Information
  unit_cost: number;
  total_cost: number;

  // Status
  status: "Pending" | "Dispatched" | "Received" | "Cancelled";

  // Validation
  available_quantity: number;
  max_transferable: number;

  // Receive Information
  received_quantity?: number;
  received_condition?: "Good" | "Damaged" | "Partially Damaged";
  receive_notes?: string;

  // Audit
  created_at: number;
  updated_at: number;
}

// Create Stock Transfer Request
export interface CreateStockTransferRequest {
  transfer_type: "Inter-Branch" | "Internal";
  source_location: {
    branch_id: string;
    warehouse_id: string;
  };
  target_location: {
    branch_id: string;
    warehouse_id: string;
  };
  items: CreateStockTransferItemRequest[];
  expected_delivery_date?: number;
  notes?: string;
  reason?: string;
}

export interface CreateStockTransferItemRequest {
  product_id?: string;
  ingredient_id?: string;
  quantity: number;
  unit: string;
  batch_id?: string;
  unit_cost: number;
}

// Update Stock Transfer Request
export interface UpdateStockTransferRequest {
  transfer_id?: string;
  expected_delivery_date?: number;
  notes?: string;
  reason?: string;
}

export interface UpdateStockTransferItemRequest {
  _id?: string;
  product_id: string;
  quantity: number;
  unit: string;
  batch_id?: string;
  unit_cost: number;
}

// Stock Transfer Filters
export interface StockTransferFilters {
  transfer_type?: "Inter-Branch" | "Internal" | "All";
  status?:
    | "Draft"
    | "Dispatched"
    | "In Transit"
    | "Received"
    | "Cancelled"
    | "All";
  date_range?: {
    start?: number;
    end?: number;
  };
  source_location?: string;
  target_location?: string;
  created_by?: string;
  search?: string;
}

// Stock Transfer List Response
export interface StockTransferListResponse {
  transfers: StockTransfer[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Location Options
export interface LocationOption {
  id: string;
  _id?: string; // Add _id for backend compatibility
  name: string;
  branch_id: string;
  branch_name: string;
  warehouse_id: string;
  warehouse_name: string;
}

// Product Option for Transfer
export interface ProductOption {
  id: string;
  name: string;
  sku: string;
  unit: string;
  available_quantity: number;
  is_batch_tracked: boolean;
}

export interface BatchOption {
  batch_id: string;
  batch_number: string;
  quantity: number;
  remaining_quantity: number;
  expiry_date?: number;
  unit_cost: number;
  total_cost: number;
}

// Dispatch/Receive Actions
export interface DispatchTransferRequest {
  transfer_id: string;
  dispatch_notes?: string;
}

export interface ReceiveTransferRequest {
  transfer_id: string;
  received_items: ReceiveTransferItem[];
  receive_notes?: string;
}

export interface ReceiveTransferItem {
  item_id: string;
  received_quantity: number;
  condition: "Good" | "Damaged" | "Partially Damaged";
  notes?: string;
}

// Validation Response
export interface TransferValidationResponse {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  item_validations: {
    item_id: string;
    is_valid: boolean;
    errors: string[];
    warnings: string[];
  }[];
}

// Transfer Summary for List View
export interface TransferSummary {
  total_items: number;
  total_value: number;
  item_preview: string; // e.g., "Flour, Oil, Salt (+2 more)"
}

// API Response Types
export interface StockTransferResponse {
  success: boolean;
  data: StockTransfer;
  message: string;
}

export interface StockTransferListApiResponse {
  success: boolean;
  data: StockTransferListResponse;
  message: string;
}

export interface LocationsResponse {
  success: boolean;
  data: LocationOption[];
  message: string;
}

export interface ProductsResponse {
  success: boolean;
  data: ProductOption[];
  message: string;
}

export interface BatchesResponse {
  success: boolean;
  data: BatchOption[];
  message: string;
}

// Form State Types
export interface StockTransferFormData {
  transfer_type: "Inter-Branch" | "Internal";
  source_location: LocationOption | null;
  target_location: LocationOption | null;
  expected_delivery_date: number | null;
  notes: string;
  reason: string;
  items: StockTransferItemFormData[];
}

export interface StockTransferItemFormData {
  product: ProductOption | null;
  quantity: number;
  batch: BatchOption | null;
  unit: string;
  unit_cost: number;
  available_quantity: number;
  max_transferable: number;
  errors: string[];
}

// Error Types
export interface StockTransferError {
  field: string;
  message: string;
  code: string;
}

// Constants
export const TRANSFER_TYPES = {
  INTER_BRANCH: "Inter-Branch" as const,
  INTERNAL: "Internal" as const,
} as const;

export const TRANSFER_STATUS = {
  DRAFT: "Draft" as const,
  DISPATCHED: "Dispatched" as const,
  IN_TRANSIT: "In Transit" as const,
  RECEIVED: "Received" as const,
  CANCELLED: "Cancelled" as const,
} as const;

export const ITEM_STATUS = {
  PENDING: "Pending" as const,
  DISPATCHED: "Dispatched" as const,
  RECEIVED: "Received" as const,
  CANCELLED: "Cancelled" as const,
} as const;

export const ITEM_CONDITION = {
  GOOD: "Good" as const,
  DAMAGED: "Damaged" as const,
  PARTIALLY_DAMAGED: "Partially Damaged" as const,
} as const;
